import { Optional } from "common/utils/types";

import Alert from "client/common/components/content/Alert";

import { SubscriptionAddressIssue, SubscriptionPaymentMethod } from "../types";
import {
  useSubscriptionAddressUpdateDrawer,
  useSubscriptionPaymentUpdateDrawer,
} from "../stackables";
import {
  getSubscriptionAddressIssueContent,
  getSubscriptionPaymentIssueContent,
  isSubscriptionAddressUnshippable,
  isSubscriptionPaymentUnchargeable,
} from "../utils";

type SubscriptionPaymentAlertProps = {
  subscription: {
    id: number;
    payment_method: Optional<SubscriptionPaymentMethod>;
  };
};

const SubscriptionPaymentAlert = ({ subscription }: SubscriptionPaymentAlertProps) => {
  const { open: openPaymentUpdate } = useSubscriptionPaymentUpdateDrawer();
  const { drawerMessage, actionLabel } = getSubscriptionPaymentIssueContent(
    subscription.payment_method,
  );

  // Stacked over the form it is blocking rather than routed to, so closing it returns the client
  // to the edit they came for.
  const onClick = () => {
    openPaymentUpdate({ id: subscription.id });
  };

  return (
    <Alert
      variant="danger"
      shouldScrollOnMount={true}
      actions={[{ label: actionLabel, onClick, hasRightIcon: true }]}
    >
      {drawerMessage}
    </Alert>
  );
};

type SubscriptionAddressAlertProps = {
  subscription: {
    id: number;
    shipping_address: Optional<SubscriptionAddressIssue>;
  };
};

const SubscriptionAddressAlert = ({ subscription }: SubscriptionAddressAlertProps) => {
  const { open: openAddressUpdate } = useSubscriptionAddressUpdateDrawer();
  const { drawerMessage, actionLabel } = getSubscriptionAddressIssueContent(
    subscription.shipping_address,
  );

  // Stacked over the form it is blocking rather than routed to, so closing it returns the client
  // to the edit they came for.
  const onClick = () => {
    openAddressUpdate({ id: subscription.id });
  };

  return (
    <Alert
      variant="danger"
      shouldScrollOnMount={true}
      actions={[{ label: actionLabel, onClick, hasRightIcon: true }]}
    >
      {drawerMessage}
    </Alert>
  );
};

type SubscriptionAlertProps = {
  subscription: {
    id: number;
    payment_method: Optional<SubscriptionPaymentMethod>;
    shipping_address: Optional<SubscriptionAddressIssue>;
  };
};

const SubscriptionAlert = ({ subscription }: SubscriptionAlertProps) => {
  if (isSubscriptionPaymentUnchargeable(subscription)) {
    return <SubscriptionPaymentAlert subscription={subscription} />;
  }

  if (isSubscriptionAddressUnshippable(subscription)) {
    return <SubscriptionAddressAlert subscription={subscription} />;
  }

  return null;
};

export default Object.assign(SubscriptionAlert, {
  Payment: SubscriptionPaymentAlert,
  Address: SubscriptionAddressAlert,
});
